import { memo } from 'react';
import { Box, Typography, Paper, Divider, Chip, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { Board, NumberMode } from '../../types';
import { getNumberSetKeys, getBoxNeighborCount } from '../../types';

export interface HelpPanelProps {
  board: Board;
}

export const HelpPanel = memo<HelpPanelProps>(({ board }) => {
  const otEnabled = board.payoutConfig?.overtimeConfig?.enabled ?? false;
  const otSplit = board.payoutConfig?.overtimeConfig?.overtimeSplit ?? 0.7;

  // Calculate profit from quarter payouts
  const quarterKeys = board.quarters === 2
    ? ['1H', '2H']
    : board.quarters === 3
      ? ['P1', 'P2', 'P3']
      : ['Q1', 'Q2', 'Q3', 'Q4'];
  const payoutTotal = quarterKeys.reduce((sum, key) => sum + (board.payoutConfig?.quarterPayouts[key] || 0), 0);
  const profitPercent = Math.max(0, (1 - payoutTotal) * 100);
  const grossPot = board.pricePerSquare * (board.totalCells || 100);
  const payoutPot = grossPot * payoutTotal;

  // Number mode settings
  const numberMode: NumberMode = board.numberMode || 'single';
  const numberSetKeys = getNumberSetKeys(board.quarters || 4, numberMode);
  const hasMultipleNumberSets = numberSetKeys.length > 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        How to Play
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Game Basics
        </Typography>
        <Typography variant="body2" paragraph>
          This is a grid of squares at <strong>${board.pricePerSquare.toFixed(2)}</strong> each.
          Winners are determined by the last digit of each team's score at the end of each quarter.
        </Typography>
        <Typography variant="body2" paragraph>
          • Rows = {board.homeOnTop ? board.awayTeam : board.homeTeam}'s score last digit (0-9)
        </Typography>
        <Typography variant="body2" paragraph>
          • Columns = {board.homeOnTop ? board.homeTeam : board.awayTeam}'s score last digit (0-9)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Link component={RouterLink} to="/support/game-mechanics#the-grid">
            Learn more about how the grid works →
          </Link>
        </Typography>
      </Paper>

      {/* Multi-Number Mode Explanation */}
      {hasMultipleNumberSets && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50', borderColor: 'primary.main', border: '1px solid' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
              Multiple Number Sets
            </Typography>
            <Chip
              label={numberMode === 'half' ? 'Half Mode' : 'Quarter Mode'}
              size="small"
              color="primary"
            />
          </Box>

          <Typography variant="body2" paragraph>
            Unlike traditional boards that use one set of numbers for the entire game, this board uses{' '}
            <strong>different numbers for different periods</strong>. This means you have more chances to win!
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            How It Works
          </Typography>
          <Typography variant="body2" paragraph>
            • The grid shows {numberSetKeys.length} rows of numbers at the top and {numberSetKeys.length} columns on the left
          </Typography>
          <Typography variant="body2" paragraph>
            • Each row/column is labeled ({numberSetKeys.join(', ')}) to show which period it applies to
          </Typography>
          <Typography variant="body2" paragraph>
            • When a score is recorded, use the numbers for that specific period to find the winning square
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Which Numbers Apply When?
          </Typography>
          <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, mb: 2 }}>
            {numberMode === 'half' && board.quarters === 4 && (
              <>
                <Typography variant="body2">• <strong>Q1 & Q2:</strong> Use the <Chip label="1H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>Q3 & Q4:</strong> Use the <Chip label="2H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'half' && board.quarters === 3 && (
              <>
                <Typography variant="body2">• <strong>Period 1:</strong> Use the <Chip label="1H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>Periods 2 & 3:</strong> Use the <Chip label="2H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'half' && board.quarters === 2 && (
              <>
                <Typography variant="body2">• <strong>1st Half:</strong> Use the <Chip label="1H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>2nd Half:</strong> Use the <Chip label="2H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'quarter' && board.quarters === 4 && (
              <>
                <Typography variant="body2">• <strong>Q1:</strong> Use <Chip label="Q1" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers &nbsp; • <strong>Q2:</strong> Use <Chip label="Q2" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>Q3:</strong> Use <Chip label="Q3" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers &nbsp; • <strong>Q4:</strong> Use <Chip label="Q4" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'quarter' && board.quarters === 3 && (
              <>
                <Typography variant="body2">• <strong>P1:</strong> Use <Chip label="P1" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>P2:</strong> Use <Chip label="P2" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>P3:</strong> Use <Chip label="P3" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'quarter' && board.quarters === 2 && (
              <>
                <Typography variant="body2">• <strong>1st Half:</strong> Use the <Chip label="1H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>2nd Half:</strong> Use the <Chip label="2H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
          </Box>

          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 1 }}>
            Tip: When you hover over a recorded score, the grid will highlight which number row/column applies.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/support/game-mechanics#multi-number-sets">
              Learn more about multi-number sets →
            </Link>
          </Typography>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Payout Structure
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Typography variant="body2">
            <strong>Gross Pot:</strong> ${grossPot.toFixed(2)}
          </Typography>
          {profitPercent > 0 && (
            <Typography variant="body2">
              <strong>Organizer Fee:</strong> {profitPercent.toFixed(0)}%
            </Typography>
          )}
          <Typography variant="body2">
            <strong>Payout Pool:</strong> ${payoutPot.toFixed(2)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Payout by Quarter
        </Typography>
        {quarterKeys.map((key, index) => {
          const pct = (board.payoutConfig?.quarterPayouts[key] || 0) * 100;
          const amount = (board.payoutConfig?.quarterPayouts[key] || 0) * grossPot;
          const isLast = index === quarterKeys.length - 1;
          const label = board.quarters === 2
            ? (index === 0 ? '1st Half' : '2nd Half / Final')
            : board.quarters === 3
              ? ['1st Period', '2nd Period', '3rd Period / Final'][index]
              : ['Q1', 'Q2 / Halftime', 'Q3', 'Q4 / Final'][index];

          return (
            <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
              <strong>{label}:</strong> {pct.toFixed(0)}% (${amount.toFixed(2)})
              {isLast && otEnabled && (
                <Typography component="span" variant="body2" color="text.secondary">
                  {' '}— Split if OT
                </Typography>
              )}
            </Typography>
          );
        })}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <Link component={RouterLink} to="/support/payouts#quarter-payouts">
            Learn more about payouts →
          </Link>
        </Typography>
      </Paper>

      {otEnabled && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50', borderColor: 'info.main', border: '1px solid' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="info.main">
            Overtime Rules
          </Typography>
          <Typography variant="body2" paragraph>
            If overtime occurs, the final payout is split: <strong>{((1 - otSplit) * 100).toFixed(0)}%</strong> for
            end of regulation, <strong>{(otSplit * 100).toFixed(0)}%</strong> for final score.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/support/payouts#overtime">
              Learn more about overtime rules →
            </Link>
          </Typography>
        </Paper>
      )}

      {/* Bad Numbers Section */}
      {board.payoutConfig?.badNumbersConfig?.enabled && (() => {
        const config = board.payoutConfig.badNumbersConfig;
        const badCombinations = config.badCombinations || [];
        // Legacy fallback: generate combinations from badDigits
        const badDigits = config.badDigits || [];
        const effectiveCombos = badCombinations.length > 0
          ? badCombinations
          : badDigits.flatMap(r => badDigits.map(c => `${r}-${c}`));
        const payoutAmount = config.payoutAmount || 0;
        const badSquareCount = effectiveCombos.length;
        const totalPayout = badSquareCount * payoutAmount;

        return (
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50', borderColor: 'success.main', border: '1px solid' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{
                width: 24,
                height: 24,
                backgroundColor: '#4caf50',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '0.85rem',
                fontWeight: 'bold',
              }}>
                ✓
              </Box>
              <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
                Bad Numbers (Automatic Winners)
              </Typography>
            </Box>
            <Typography variant="body2" paragraph>
              This board has <strong>Bad Numbers</strong> enabled. Certain digit combinations are considered
              "unlucky" and receive automatic payouts when the board locks (before the game even starts).
              Bad Numbers are <strong>right-way only</strong> — the combination 2-5 only pays when row=2 and column=5, not when reversed.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Payout per bad square:</strong> ${payoutAmount.toFixed(2)}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Total:</strong> {badSquareCount} bad square{badSquareCount !== 1 ? 's' : ''} × ${payoutAmount.toFixed(2)} = ${totalPayout.toFixed(2)}
            </Typography>

            {/* Bad Combinations Display */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Bad Combinations:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                {effectiveCombos.sort().map((combo) => (
                  <Chip
                    key={combo}
                    label={combo}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      bgcolor: '#ef5350',
                      color: '#fff',
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Format: Row-Column (e.g., 2-5 means row digit 2, column digit 5)
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              A square is "bad" if its row-column digit combination (after randomization) is in the list above.
              Payouts are credited automatically when the board locks — no action needed!
            </Typography>
          </Paper>
        );
      })()}

      {/* Box Neighbor Payouts Section */}
      {board.payoutConfig?.boxNeighborPayouts?.enabled && (() => {
        const config = board.payoutConfig.boxNeighborPayouts;
        const boxSize = config.boxSize;
        const neighborCount = getBoxNeighborCount(boxSize);
        // Calculate box dimensions correctly for all sizes
        const boxWidth = boxSize === '2x2' ? 2 : 5;
        const boxHeight = boxSize === '5x5' ? 5 : 2;
        const colGroups = 10 / boxWidth;
        const rowGroups = 10 / boxHeight;
        const squaresPerBox = boxWidth * boxHeight;

        // Calculate payout info
        const usePerQuarter = config.usePerQuarterAmounts && config.quarterAmounts;
        const globalAmount = config.globalAmount || 0;
        const quarterAmounts = config.quarterAmounts || {};

        return (
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50', borderColor: 'info.main', border: '1px solid' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{
                width: 24,
                height: 24,
                border: '3px solid #1976d2',
                borderRadius: 1,
              }} />
              <Typography variant="subtitle1" fontWeight="bold" color="info.dark">
                Box Neighbor Payouts
              </Typography>
            </Box>

            <Typography variant="body2" paragraph>
              This board uses <strong>Box Neighbors</strong>. The 10×10 grid is divided into fixed <strong>{boxSize}</strong> groups.
              When a square wins, the other squares in the same box also receive a payout.
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>Box size:</strong> {squaresPerBox} squares per box ({neighborCount} neighbors + 1 winner)
            </Typography>

            {/* Visual box layout */}
            <Box sx={{ my: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>Box Layout:</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${colGroups}, 1fr)`,
                  gridTemplateRows: `repeat(${rowGroups}, 1fr)`,
                  gap: '3px',
                  width: boxSize === '2x2' ? 140 : boxSize === '5x5' ? 140 : 200,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 0.75,
                  bgcolor: 'grey.100',
                }}
              >
                {Array.from({ length: rowGroups * colGroups }).map((_, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      aspectRatio: boxSize === '5x2' ? '2.5' : '1',
                      bgcolor: idx % 2 === (Math.floor(idx / colGroups) % 2) ? 'info.light' : 'info.main',
                      borderRadius: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {squaresPerBox}
                  </Box>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Each box contains {squaresPerBox} squares
              </Typography>
            </Box>

            {/* Payout amounts */}
            <Box sx={{ my: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>Payout per Neighbor:</Typography>
              {usePerQuarter ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {quarterKeys.map(key => {
                    const amount = quarterAmounts[key] || 0;
                    return amount > 0 ? (
                      <Chip key={key} label={`${key}: $${amount.toFixed(2)}`} size="small" color="info" />
                    ) : null;
                  })}
                </Box>
              ) : (
                <Typography variant="body2">
                  ${globalAmount.toFixed(2)} per neighbor per quarter
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Each neighbor receives the full amount (not split among neighbors)
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Example: If your square wins Q1, the {neighborCount} other squares in your box each get the neighbor payout for Q1.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <Link component={RouterLink} to="/support/payouts#box-neighbor-payouts">
                Learn more about box neighbor payouts →
              </Link>
            </Typography>
          </Paper>
        );
      })()}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Square Colors
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#ffffff', border: '1px solid #ccc', borderRadius: 0.5 }} />
            <Typography variant="body2">Available</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#2196f3', borderRadius: 0.5 }} />
            <Typography variant="body2">Yours</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#ff9800', borderRadius: 0.5 }} />
            <Typography variant="body2">Others</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#4caf50', borderRadius: 0.5 }} />
            <Typography variant="body2">Winner</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Quick Tips
        </Typography>
        <Typography variant="body2" paragraph>
          • In football, 0, 3, and 7 are more common ending digits
        </Typography>
        <Typography variant="body2" paragraph>
          • Numbers are randomized after all squares are claimed
        </Typography>
        <Typography variant="body2">
          • Share invite code <strong>{board.inviteCode}</strong> to let friends join
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <Link component={RouterLink} to="/support/game-mechanics#probabilities">
            Learn more about number probabilities →
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
});

HelpPanel.displayName = 'HelpPanel';

export default HelpPanel;
